import {
  BlockStyle,
  ComponentType,
  ESdChangeReason,
  IActiveKuikaComponent,
  ICloneItemMessage,
  IComponentDefinition,
  ICopyItemMessage,
  IDisplayItem,
  IDisplayItemComponent,
  IDragDropByCreateItemMessage,
  IDragDropByMoveItemMessage,
  IHighLightHoveredItemMessage,
  IPasteItemMessage,
  IPasteItemStyleMessage,
  IRemoveItemMessage,
  IScreenDesign,
  IScreenDesignDictionary,
  ITreeItem,
  PlaceToType,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { resolveChildItemsHost } from "../../../shared/utility/component-children-host";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../shared/utility/screen-design-helper";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface DisplayItemProviderState {
  getSelectedDisplayItem: (sd: IScreenDesign) => IDisplayItem | undefined;
  selectedKuikaIds: string[];
  setSelectedDisplayItem: (kuikaId: string) => void;
  setSelectedTabItem: (tabKuikaId: string, tabItemKuikaId: string, index: number) => void;
  setSelectedStepItem: (stepKuikaId: string, stepItemKuikaId: string, index: number) => void;
  setSelectedCollapseItem: (collapseKuikaId: string, collapseItemKuikaId: string) => void;
  highlightHoveredItem: (kuikaId: string) => void;
  addChildItemByComponentName: (
    kuikaId: string,
    componentName: string,
    placeToType?: PlaceToType,
    kuikaIdToFocus?: string
  ) => void;
  deleteItemByKuikaId: (kuikaId: string, kuikaIdToFocus?: string) => void;
  reOrderItems: (sourceKuikaId: string, targetKuikaId: string, nextSelectedDiId: string) => void;
  cloneDI: (kuikaid: string) => void;
  getActiveKuikaComponent: () => IActiveKuikaComponent;
  componentChildrenSettingsMainDi?: IDisplayItem;
  getComponentDefinition: (diComponent: IDisplayItemComponent | undefined) => IComponentDefinition | undefined;
  hasASelectedComponent: boolean;
  doesSelectedComponentHasStyleProp: boolean;
  addNavigationComponent: (componentId: string) => IDisplayItem | undefined;
  dragDropByCreateItemMessage: (message: IDragDropByCreateItemMessage) => void;
  dragDropByMoveItemMessage: (message: IDragDropByMoveItemMessage) => void;
  removeItemMessage: (message: IRemoveItemMessage) => void;
  copyItemMessage: (message: ICopyItemMessage) => void;
  pasteItemMessage: (message: IPasteItemMessage) => void;
  pasteItemStyleMessage: (message: IPasteItemStyleMessage) => void;
  cloneItemMessage: (message: ICloneItemMessage) => void;
  getClipboardValue: () => void;
  getElementIsDraggable: (
    targetComponent: IDisplayItemComponent,
    targetComponentDefinition?: IComponentDefinition,
    sourceComponentDefinition?: IComponentDefinition,
    targetDI?: IDisplayItem,
    sd?: IScreenDesign,
    sdDict?: IScreenDesignDictionary,
    message?: IDragDropByMoveItemMessage | IDragDropByCreateItemMessage,
    sourceDI?: IDisplayItem,
    simpleTargetKuikaId?: string,
    simpleSourceKuikaId?: string,
    doNotTriggerNotifications?: boolean
  ) => boolean;
  detachForm: (kuikaId: string) => void;
  renamingDisplayItemId?: number;
  setRenamingDisplayItemId: (id?: number) => void;
  setComponentChildrenSettingsMainDi: (kuikaid: string, updateState?: boolean) => IDisplayItem | undefined;
  setAiResponseToBeGeneratedFromImage: (content: string) => void;
}

export class DisplayItemProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  DisplayItemProviderState
> {
  static defaultState: DisplayItemProviderState = {
    getSelectedDisplayItem: () => {
      return undefined;
    },
    selectedKuikaIds: [],
    setSelectedDisplayItem: () => {},
    setSelectedTabItem: () => {},
    setSelectedStepItem: () => {},
    setSelectedCollapseItem: () => {},
    highlightHoveredItem: () => {},
    addChildItemByComponentName: () => {},
    deleteItemByKuikaId: () => {},
    reOrderItems: () => {},
    cloneDI: () => {},
    getActiveKuikaComponent: () => {
      return {} as IActiveKuikaComponent;
    },
    getComponentDefinition: () => {
      return undefined;
    },
    hasASelectedComponent: false,
    doesSelectedComponentHasStyleProp: true,
    addNavigationComponent: () => {
      return undefined;
    },
    dragDropByMoveItemMessage: () => {},
    dragDropByCreateItemMessage: () => {},
    removeItemMessage: () => {},
    copyItemMessage: () => {},
    pasteItemMessage: () => {},
    pasteItemStyleMessage: () => {},
    cloneItemMessage: () => {},
    getClipboardValue: () => {},
    getElementIsDraggable: () => {
      return true;
    },
    detachForm: () => {},
    setRenamingDisplayItemId: () => {},
    setAiResponseToBeGeneratedFromImage: () => {},
    setComponentChildrenSettingsMainDi: () => {
      return undefined;
    }
  };

  getProviderValues = (): DisplayItemProviderState => {
    return {
      getSelectedDisplayItem: this.getSelectedDisplayItem,
      selectedKuikaIds: this.state.selectedKuikaIds,
      setSelectedDisplayItem: this.setSelectedDisplayItem,
      setSelectedTabItem: this.setSelectedTabItem,
      setSelectedStepItem: this.setSelectedStepItem,
      setSelectedCollapseItem: this.setSelectedCollapseItem,
      highlightHoveredItem: this.handleHighlightHoveredItem,
      addChildItemByComponentName: this.addChildItemByComponentName,
      deleteItemByKuikaId: this.deleteItemByKuikaId,
      reOrderItems: this.reOrderItems,
      cloneDI: this.cloneDI,
      getActiveKuikaComponent: this.getActiveKuikaComponent,
      componentChildrenSettingsMainDi: this.state.componentChildrenSettingsMainDi,
      getComponentDefinition: this.getComponentDefinition,
      hasASelectedComponent: this.state.hasASelectedComponent,
      doesSelectedComponentHasStyleProp: this.state.doesSelectedComponentHasStyleProp,
      addNavigationComponent: this.addNavigationComponent,
      dragDropByMoveItemMessage: this.dragDropByMoveItemMessage,
      dragDropByCreateItemMessage: this.dragDropByCreateItemMessage,
      removeItemMessage: this.removeItemMessage,
      copyItemMessage: this.copyItemMessage,
      pasteItemMessage: this.pasteItemMessage,
      pasteItemStyleMessage: this.pasteItemStyleMessage,
      cloneItemMessage: this.cloneItemMessage,
      getClipboardValue: this.getClipboardValue,
      getElementIsDraggable: this.getElementIsDraggable,
      detachForm: this.detachForm,
      renamingDisplayItemId: this.state.renamingDisplayItemId,
      setRenamingDisplayItemId: this.setRenamingDisplayItemId,
      setComponentChildrenSettingsMainDi: this.setComponentChildrenSettingsMainDi,
      setAiResponseToBeGeneratedFromImage: this.setAiResponseToBeGeneratedFromImage
    };
  };

  generatePanelDi = (parentId: number): IDisplayItem => {
    const panel = ScreenDesignHelper.createNewComponentByName("Panel", this.state.project);

    if (!panel) {
      throw new Error("Panel component not found");
    }

    return ScreenDesignHelper.createNewDisplayItemByComponent(
      this.partial.dragDrop.screenContext.screenProvider.getScreenDesign(),
      this.partial.dragDrop.screenContext.screenProvider.getScreenDesignDictionary(),
      parentId,
      panel,
      this.state.project.componentSetObj.components
    );
  };

  detachForm = (kuikaId: string) => {
    const di = this.getSelectedDisplayItem();

    if (!di || !di.parentId || di.id.toString() !== kuikaId || di.item.name !== "Form") {
      return;
    }

    const panel = this.generatePanelDi(di.parentId);
    panel.children = _.cloneDeep(di.children);
    panel.children.forEach((c) => (c.parentId = panel.id));

    this.partial.frame.replaceDi({
      targetKuikaId: kuikaId,
      displayItem: panel
    });
  };

  getSelectedDisplayItem = (): IDisplayItem | undefined => {
    if (!KMainFunctions.isArrHasValue(this.state.selectedKuikaIds)) return;
    const di = ScreenDesignHelper.getSelectedDisplayItem(
      this.state.screenDesignDictionary,
      this.state.selectedKuikaIds
    );
    return di;
  };

  setSelectedDisplayItem = (kuikaId: string): void => {
    if (this.state.selectedKuikaIds[0] === kuikaId) {
      return;
    }
    this.partial.screenProvider.setScreenData(ESdChangeReason.ChangeSelectedDiByTools, kuikaId);
  };

  setSelectedTabItem = (tabKuikaId: string, tabItemKuikaId: string, index: number): void => {
    this.partial.frame.setSelectedTabItem({ kuikaId: tabKuikaId, index });

    setTimeout(() => {
      this.setSelectedDisplayItem(tabItemKuikaId);
    }, 250);
  };

  setSelectedStepItem = (stepKuikaId: string, stepItemKuikaId: string, index: number): void => {
    this.partial.frame.setSelectedStepItem({ kuikaId: stepKuikaId, index });

    setTimeout(() => {
      this.setSelectedDisplayItem(stepItemKuikaId);
    }, 250);
  };

  setSelectedCollapseItem = (collapseKuikaId: string, collapseItemKuikaId: string): void => {
    this.partial.frame.setSelectedCollapseItem({ collapseKuikaId, collapseItemKuikaId });

    setTimeout(() => {
      this.setSelectedDisplayItem(collapseItemKuikaId);
    }, 250);
  };

  setComponentChildrenSettingsMainDi = (kuikaid: string, updateState?: boolean, _dict?: any) => {
    // The walk itself lives in `component-children-host`, shared with the workflow screen builder,
    // which has to answer the same question about a screen this provider is not looking at. A
    // second copy would mean a catalog change honoured in the right panel and ignored in the Forms
    // tab, for no reason a user could explain.
    const dict = _dict || this.partial.dragDrop.screenContext.screenProvider.getScreenDesignDictionary();
    const parentDI: IDisplayItem | undefined = resolveChildItemsHost(
      dict,
      (item) => this.partial.dragDrop.getComponentDefinition(item),
      kuikaid
    );
    if (updateState) {
      this.setState({ componentChildrenSettingsMainDi: parentDI });
    }
    return parentDI;
  };

  handleHighlightHoveredItem = (kuikaId: string): void => {
    this.partial.frame.highlightHoveredItem({
      kuikaId
    } as IHighLightHoveredItemMessage);
  };

  addChildItemByComponentName = (
    kuikaId: string,
    componentName: string,
    placeToType?: PlaceToType,
    kuikaIdToFocus?: string
  ): void => {
    this.partial.dragDrop.addChildItemByComponentName(kuikaId, componentName, placeToType, kuikaIdToFocus);
  };

  deleteItemByKuikaId = (kuikaId: string, kuikaIdToFocus?: string) => {
    this.partial.dragDrop.deleteItemByKuikaId(kuikaId, kuikaIdToFocus);
  };

  reOrderItems = (sourceKuikaId: string, targetKuikaId: string, nextSelectedDiId: string) => {
    this.partial.dragDrop.reOrderItems(sourceKuikaId, targetKuikaId, nextSelectedDiId);
  };

  dragDropByMoveItemMessage = (message: IDragDropByMoveItemMessage) => {
    this.partial.dragDrop.dragDropByMoveItemMessage(message);
  };

  dragDropByCreateItemMessage = (message: IDragDropByCreateItemMessage) => {
    this.partial.dragDrop.dragDropByCreateItemMessage(message);
  };

  removeItemMessage = (message: IRemoveItemMessage) => {
    this.partial.frameMessageReceiver.removeItemMessage(message);
  };

  copyItemMessage = (message: ICopyItemMessage) => {
    this.partial.frameMessageReceiver.copyItemMessage(message);
  };

  pasteItemMessage = (message: IPasteItemMessage) => {
    this.partial.frameMessageReceiver.pasteItemMessage(message);
  };

  pasteItemStyleMessage = (message: IPasteItemStyleMessage) => {
    this.partial.frameMessageReceiver.pasteItemStyleMessage(message);
  };

  cloneItemMessage = (message: ICloneItemMessage) => {
    this.partial.frameMessageReceiver.cloneItemMessage(message);
  };

  getClipboardValue = () => {
    return this.partial.dragDrop.getClipboardValue();
  };

  cloneDI = (kuikaid: string): void => {
    this.partial.dragDrop.cloneDI(kuikaid);
  };

  getActiveKuikaComponent = () => {
    if (!this.state || !KMainFunctions.isArrHasValue(this.state.selectedKuikaIds)) {
      return {} as IActiveKuikaComponent;
    }
    const sd = this.partial.screenProvider.getScreenDesign();
    const selectedDisplayItem = this.getSelectedDisplayItem();
    if (!selectedDisplayItem) {
      return {} as IActiveKuikaComponent;
    }
    const kuikaId = selectedDisplayItem.id.toString();
    const tree: ITreeItem[] = this.getTreeItems(selectedDisplayItem);
    const activeComponent = this.getComponentDefinition(selectedDisplayItem.item);
    if (activeComponent) {
      return {
        kuikaId,
        name: activeComponent.name,
        blockStyle: activeComponent.blockStyle,
        componentType: activeComponent.componentType,
        tree,
        friendlyName: selectedDisplayItem.friendlyName
      } as IActiveKuikaComponent;
    }
    return {
      kuikaId,
      name: "String",
      blockStyle: BlockStyle.inlineBlock,
      componentType: ComponentType.simple,
      tree
    } as IActiveKuikaComponent;
  };

  getTreeItems = (displayItem: IDisplayItem | undefined): ITreeItem[] => {
    const dict = this.partial.screenProvider.getScreenDesignDictionary();
    if (displayItem === undefined || !displayItem.id) {
      return [] as ITreeItem[];
    }
    let di: IDisplayItem | undefined = _.clone(displayItem);
    const result: ITreeItem[] = [];
    while (di !== undefined && di.id) {
      const treeItem: ITreeItem = this.getTreeFromDI(di);
      result.unshift(treeItem);
      if (di.parentId) {
        const parent = dict[di.parentId];
        if (parent) {
          di = _.clone(parent);
        } else {
          di = undefined;
        }
      } else {
        di = undefined;
      }
    }
    return result;
  };

  getTreeFromDI = (di: IDisplayItem | undefined): ITreeItem => {
    const dict = this.partial.screenProvider.getScreenDesignDictionary();
    const tree: ITreeItem = {} as ITreeItem;
    if (di !== undefined) {
      if (!di.id) {
        return tree;
      }
      if (di.friendlyName) {
        tree.friendlyName = di.friendlyName;
      }
      tree.kuikaId = di.id.toString();
      tree.name = this.getNameFromDi(di);
      if (di.parentId) {
        tree.parentKuikaId = di.parentId.toString();
      } else {
        tree.parentKuikaId = "";
      }
      tree.children = [];
      di?.children?.map((childDi) => {
        if (childDi.item) {
          const obj = {
            friendlyName: childDi.friendlyName,
            kuikaId: childDi.id.toString(),
            name: this.getNameFromDi(childDi)
          } as ITreeItem;
          tree.children.push(obj);
        }
      });
      if (di.id === SpecialDIIds.rootDI) {
        const sd = this.partial.screenProvider.getScreenDesign();
        // const headerDI: IDisplayItem | undefined = ScreenDesignHelper.findDisplayItemBykuikaid(
        //   SpecialDIIds.headerDI.toString(),
        //   sd.displayItems
        // );
        const headerDI = dict[SpecialDIIds.headerDI];
        if (headerDI) {
          tree.children.unshift({
            friendlyName: headerDI.friendlyName,
            kuikaId: headerDI.id.toString(),
            name: this.getNameFromDi(headerDI)
          } as ITreeItem);
        }

        const pageHeaderDI = dict[SpecialDIIds.pageHeaderDI];
        if (pageHeaderDI) {
          const insertIdx = headerDI ? 1 : 0;
          tree.children.splice(insertIdx, 0, {
            friendlyName: pageHeaderDI.friendlyName,
            kuikaId: pageHeaderDI.id.toString(),
            name: this.getNameFromDi(pageHeaderDI)
          } as ITreeItem);
        }

        const pageFooterDI = dict[SpecialDIIds.pageFooterDI];
        if (pageFooterDI) {
          tree.children.push({
            friendlyName: pageFooterDI.friendlyName,
            kuikaId: pageFooterDI.id.toString(),
            name: this.getNameFromDi(pageFooterDI)
          } as ITreeItem);
        }

        const footerDI = dict[SpecialDIIds.footerDI];
        if (footerDI) {
          tree.children.push({
            friendlyName: footerDI.friendlyName,
            kuikaId: footerDI.id.toString(),
            name: this.getNameFromDi(footerDI)
          } as ITreeItem);
        }
      }
    }
    return tree;
  };

  getNameFromDi = (di: IDisplayItem): string => {
    const component = this.getComponentDefinition(di.item);
    let componentName: string = "";
    if (component) {
      componentName = component.name;
    }
    return componentName;
  };

  getComponentDefinition = (diComponent: IDisplayItemComponent | undefined): IComponentDefinition | undefined => {
    if (!diComponent) return undefined;
    if (this.state.project && this.state.project.componentSetObj && this.state.project.componentSetObj.components) {
      const result: IComponentDefinition | undefined = ScreenDesignHelper.getComponentDefinition(
        diComponent,
        this.state.project.componentSetObj.components
      );
      return result;
    }
  };

  addNavigationComponent = (componentId: string): IDisplayItem | undefined => {
    const result = this.partial.dragDrop.addNavigationComponent(componentId, this.state.selectedKuikaIds[0]);
    return result;
  };

  getElementIsDraggable = (
    targetComponent: IDisplayItemComponent,
    targetComponentDefinition?: IComponentDefinition,
    sourceComponentDefinition?: IComponentDefinition,
    targetDI?: IDisplayItem,
    sd?: IScreenDesign,
    sdDict?: IScreenDesignDictionary,
    message?: IDragDropByMoveItemMessage | IDragDropByCreateItemMessage,
    sourceDI?: IDisplayItem,
    simpleTargetKuikaId?: string,
    simpleSourceKuikaId?: string,
    doNotTriggerNotifications?: boolean
  ) => {
    return this.partial.dragDrop.getElementIsDroppable(
      targetComponent,
      targetComponentDefinition,
      sourceComponentDefinition,
      targetDI,
      sd,
      sdDict,
      message,
      sourceDI,
      simpleTargetKuikaId,
      simpleSourceKuikaId,
      doNotTriggerNotifications
    );
  };

  setRenamingDisplayItemId = (id?: number) => {
    this.setState({ renamingDisplayItemId: id });
  };

  setAiResponseToBeGeneratedFromImage = (content: string) => {
    this.partial.aiImageToScreenGenerator.setAiResponseToBeGenerated({
      json: JSON.parse(content)
    });
  };
}
